import React, { useState, useRef, useEffect } from 'react';
import { FaMicrophone, FaStop, FaUpload, FaTrash, FaPlay, FaPause, FaCheck, FaWaveSquare, FaMagic } from 'react-icons/fa';
import SoundWave from './SoundWave';
import './VoiceCloning.css';

const VoiceCloning = ({ api, token, onVoiceCloned, showToast, currentVoiceId, onSelectVoice }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioSamples, setAudioSamples] = useState([]);
  const [voiceName, setVoiceName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [clonedVoices, setClonedVoices] = useState([]);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(new Audio());
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchClonedVoices();
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const fetchClonedVoices = async () => {
    try {
      const response = await fetch(`${api}/cloned_voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const voices = await response.json();
        setClonedVoices(voices);
      }
    } catch (error) {
      console.error('Error fetching cloned voices:', error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        setAudioSamples(prev => [...prev, {
          id: Date.now(),
          blob: audioBlob,
          url: audioUrl,
          name: `Recording ${audioSamples.length + 1}`,
          duration: recordingTime
        }]);
        
        stream.getTracks().forEach(track => track.stop());
        setRecordingTime(0);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      // Start recording timer
      const startTime = Date.now();
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTime) / 1000));
      }, 100);
    } catch (error) {
      console.error('Error starting recording:', error);
      showToast('Failed to start recording. Please allow microphone access.', 'error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach((file, index) => {
      if (file.type.startsWith('audio/')) {
        const url = URL.createObjectURL(file);
        setAudioSamples(prev => [...prev, {
          id: Date.now() + index,
          blob: file,
          url: url,
          name: file.name,
          duration: 0
        }]);
      }
    });
    event.target.value = ''; // Reset file input
  };

  const removeSample = (id) => {
    const sample = audioSamples.find(s => s.id === id);
    if (sample) {
      URL.revokeObjectURL(sample.url);
    }
    setAudioSamples(prev => prev.filter(sample => sample.id !== id));
  };

  const playAudio = (url, id) => {
    if (playingAudio === id) {
      audioRef.current.pause();
      setPlayingAudio(null);
    } else {
      audioRef.current.src = url;
      audioRef.current.play();
      setPlayingAudio(id);
      
      audioRef.current.onended = () => {
        setPlayingAudio(null);
      };
    }
  };

  const cloneVoice = async () => {
    if (!voiceName.trim()) {
      showToast('Please enter a name for your voice', 'error');
      return;
    }

    if (audioSamples.length < 1) {
      showToast('Please record or upload at least one audio sample', 'error');
      return;
    }

    setIsCloning(true);
    
    try {
      const formData = new FormData();
      formData.append('name', voiceName);
      formData.append('description', `Custom voice: ${voiceName}`);
      
      // Add audio samples
      for (let i = 0; i < audioSamples.length; i++) {
        formData.append(`audio_${i}`, audioSamples[i].blob, `sample_${i}.wav`);
      }

      const response = await fetch(`${api}/clone_voice`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        showToast('Voice cloned successfully!', 'success');
        setAudioSamples([]);
        setVoiceName('');
        fetchClonedVoices();
        if (onVoiceCloned) {
          onVoiceCloned(result.voice_id);
        }
      } else {
        const error = await response.text();
        showToast(`Failed to clone voice: ${error}`, 'error');
      }
    } catch (error) {
      console.error('Error cloning voice:', error);
      showToast('Error cloning voice', 'error');
    } finally {
      setIsCloning(false);
    }
  };

  const deleteVoice = async (voiceId) => {
    if (window.confirm('Are you sure you want to delete this voice?')) {
      try {
        const response = await fetch(`${api}/cloned_voice/${voiceId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.ok) {
          showToast('Voice deleted successfully', 'success');
          fetchClonedVoices();
        } else {
          showToast('Failed to delete voice', 'error');
        }
      } catch (error) {
        console.error('Error deleting voice:', error);
        showToast('Error deleting voice', 'error');
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-cloning-container">
      <div className="vc-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <SoundWave isActive={true} variant="gradient" size="large" />
        </div>
        <h3>Voice Cloning Studio</h3>
        <p className="vc-subtitle">Transform your voice into a digital twin with AI-powered cloning technology</p>
      </div>

      <div className="vc-section">
        <label className="vc-label">Voice Name</label>
        <input
          type="text"
          className="vc-input"
          placeholder="Enter a name for your voice"
          value={voiceName}
          onChange={(e) => setVoiceName(e.target.value)}
          disabled={isCloning}
        />
      </div>

      <div className="vc-section">
        <label className="vc-label">Audio Samples</label>
        <div className="vc-controls">
          <button
            className={`vc-btn ${isRecording ? 'vc-btn-danger' : 'vc-btn-primary'}`}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isCloning}
          >
            {isRecording ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaStop /> 
                <span>Stop Recording</span>
                <SoundWave isActive={true} variant="danger" size="small" />
                <span style={{ fontWeight: 700 }}>({formatTime(recordingTime)})</span>
              </div>
            ) : (
              <>
                <FaMicrophone /> Record Sample
              </>
            )}
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button
            className="vc-btn vc-btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCloning}
          >
            <FaUpload /> Upload Audio
          </button>
        </div>

        {audioSamples.length > 0 && (
          <div className="vc-samples">
            {audioSamples.map((sample, index) => (
              <div key={sample.id} className="vc-sample">
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 10 }}>
                  <span className="vc-sample-name">{sample.name}</span>
                  {playingAudio === sample.id && (
                    <div style={{ display: 'inline-flex' }}>
                      <SoundWave isActive={true} variant="default" size="small" />
                    </div>
                  )}
                </div>
                <div className="vc-sample-controls">
                  <button
                    className="vc-icon-btn"
                    onClick={() => playAudio(sample.url, sample.id)}
                  >
                    {playingAudio === sample.id ? <FaPause /> : <FaPlay />}
                  </button>
                  <button
                    className="vc-icon-btn vc-icon-btn-danger"
                    onClick={() => removeSample(sample.id)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="vc-section">
        <button
          className="vc-btn vc-btn-gradient"
          onClick={cloneVoice}
          disabled={isCloning || !voiceName || audioSamples.length === 0}
        >
          {isCloning ? (
            <>Processing Voice Clone...</>
          ) : (
            <>
              <FaMagic /> Clone My Voice
            </>
          )}
        </button>
        {audioSamples.length === 0 && (
          <div style={{
            marginTop: 12,
            padding: '12px 16px',
            background: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: 8,
            fontSize: 13,
            color: 'rgba(255, 193, 7, 0.9)',
            textAlign: 'center'
          }}>
            💡 Tip: Record 10-30 seconds of clear speech for best results
          </div>
        )}
      </div>

      {clonedVoices.length > 0 && (
        <div className="vc-section">
          <label className="vc-label">Your Cloned Voices</label>
          <div className="vc-voices-list">
            {clonedVoices.map((voice) => (
              <div key={voice.voice_id} className="vc-voice-item">
                <span className="vc-voice-name">{voice.name}</span>
                <div className="vc-voice-controls">
                  {currentVoiceId === voice.voice_id && (
                    <span className="vc-active-badge">
                      <FaCheck /> Active
                    </span>
                  )}
                  <button
                    className="vc-btn vc-btn-small"
                    onClick={() => onSelectVoice(voice.voice_id)}
                    disabled={currentVoiceId === voice.voice_id}
                  >
                    Use Voice
                  </button>
                  <button
                    className="vc-icon-btn vc-icon-btn-danger"
                    onClick={() => deleteVoice(voice.voice_id)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceCloning;